// What the uploader watches while the worker carries a document through the
// pipeline. Every stage advances on the job's real status, every value on the
// verdict panel is a column the worker wrote. Nothing here is simulated.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchJob, fetchPlan } from './ingestApi';

// Elements a focus trap can land on.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const POLL_MS = 1200;

const TERMINAL = ['awaiting_review', 'indexed_only', 'committed', 'duplicate', 'rejected'];
const BROKEN = ['failed', 'blocked'];
const COMPLETE = ['awaiting_review', 'indexed_only', 'committed'];

interface JobData {
  status: string;
  sha256?: string;
  doc_type?: string;
  doc_type_confidence?: number;
  risk_class?: string;
  urgency_class?: string;
  target_entity_type?: string;
  entity_confidence?: number;
  byte_size?: number;
  planCount?: number | null;
  warningCount?: number | null;
}

interface StageDef {
  name: string;
  state: string;
  out: string;
}

function isSettledFn(status: string): boolean {
  return TERMINAL.includes(status) || BROKEN.includes(status);
}

const extOf = (filename: string | undefined) => {
  const dot = String(filename || '').lastIndexOf('.');
  return dot > 0 ? filename.slice(dot + 1).toUpperCase().slice(0, 4) : 'FILE';
};

const kb = (bytes: number | null | undefined) =>
  bytes == null ? '' : bytes >= 1048576
    ? `${(bytes / 1048576).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const pct = (conf: number | null | undefined) => (conf == null ? null : `${Math.round(Number(conf) * 100)}%`);

const humanize = (s: string | undefined) => String(s || '').replace(/_/g, ' ');

function stagesFor(job: JobData | null, extra: { warningCount?: number | null } = {}): StageDef[] {
  if (!job) {
    return [
      { name: 'Fingerprint & dedupe', state: 'run', out: '' },
      { name: 'Classify document', state: 'todo', out: '' },
      { name: 'Extract fields', state: 'todo', out: '' },
      { name: 'Check against contract', state: 'todo', out: '' },
      { name: 'Build mutation plan', state: 'todo', out: '' },
    ];
  }

  const { status } = job;
  const broken = BROKEN.includes(status);
  const indexed = status === 'indexed_only';
  const stageCount = indexed ? 4 : 5;

  let done = 1;
  if (job.doc_type) done = 2;
  if (COMPLETE.includes(status)) done = stageCount;

  const running = status === 'classifying' ? 1 : status === 'extracting' ? 2 : -1;

  const at = (i: number): string => {
    if (i < done) return 'done';
    if (broken && i === done) return 'fail';
    if (!broken && i === running) return 'run';
    return 'todo';
  };

  const typeOut = job.doc_type
    ? [humanize(job.doc_type), pct(job.doc_type_confidence)].filter(Boolean).join(' · ')
    : '';

  const contractOut = !COMPLETE.includes(status)
    ? ''
    : extra.warningCount == null
      ? ''
      : extra.warningCount === 0
        ? 'no issues flagged'
        : `${extra.warningCount} issue${extra.warningCount === 1 ? '' : 's'} flagged`;

  const base: StageDef[] = [
    { name: 'Fingerprint & dedupe', state: at(0), out: job.sha256 ? `sha256 ${job.sha256.slice(0, 12)}…` : '' },
    { name: 'Classify document', state: at(1), out: typeOut },
  ];

  if (indexed) {
    return [
      ...base,
      { name: 'Chunk & embed', state: at(2), out: '' },
      { name: 'File to contract shelf', state: at(3), out: 'no approval needed' },
    ];
  }

  return [
    ...base,
    { name: 'Extract fields', state: at(2), out: '' },
    { name: 'Check against contract', state: at(3), out: contractOut },
    {
      name: 'Build mutation plan',
      state: at(4),
      out: job.planCount != null ? `${job.planCount} proposed change${job.planCount === 1 ? '' : 's'}` : '',
    },
  ];
}

const OUTCOME: Record<string, string> = {
  awaiting_review: 'Ready for review in the Ingest Queue.',
  indexed_only: 'Filed and indexed — no approval needed.',
  committed: 'Committed.',
  duplicate: 'Already ingested — skipped as a duplicate.',
  rejected: 'Dismissed.',
  failed: 'Processing failed. Retry it from the Ingest Queue.',
  blocked: 'Blocked — resolve the blocker in the Ingest Queue.',
};

interface Upload {
  jobId: string;
  filename: string;
}

interface ProcessingOverlayProps {
  uploads: Upload[];
  onClose: () => void;
}

export default function ProcessingOverlay({ uploads, onClose }: ProcessingOverlayProps) {
  const [jobs, setJobs] = useState<Array<{ jobId: string; filename: string; job: JobData | null }>>(() => uploads.map((u) => ({ ...u, job: null })));
  const [index, setIndex] = useState(0);
  const [waited, setWaited] = useState(0);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<Element | null>(null);

  const current = jobs[index] || jobs[0] || null;
  const job = current?.job || null;
  const settled = job ? isSettledFn(job.status) : false;
  const allSettled = jobs.every((j) => j.job && isSettledFn(j.job.status));

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      const pending = jobs.filter((j) => !j.job || !isSettledFn(j.job.status));
      if (!pending.length) return;

      const fetched = await Promise.all(
        pending.map(async (j) => {
          try {
            const { job: fresh } = await fetchJob(j.jobId);
            if (!fresh) return null;
            if (fresh.status === 'awaiting_review') {
              const { plan, items } = await fetchPlan(j.jobId).catch(() => ({ plan: null, items: [] }));
              (fresh as JobData).planCount = Array.isArray(items) ? items.length : null;
              (fresh as JobData).warningCount = plan && fresh.doc_type === 'invoice'
                ? (Array.isArray(plan.warnings) ? plan.warnings.length : 0) + (plan.rate_flags?.anyOver ? plan.rate_flags.overCount || 1 : 0)
                : null;
            }
            return { jobId: j.jobId, fresh };
          } catch {
            return null;
          }
        })
      );

      if (cancelled) return;
      const byId = new Map(fetched.filter(Boolean).map((f) => [f!.jobId, f!.fresh]));
      if (byId.size) {
        setJobs((prev) => prev.map((j) => (byId.has(j.jobId) ? { ...j, job: byId.get(j.jobId)! } : j)));
      }
      setWaited((w) => w + POLL_MS);
    };

    tick();
    timer.current = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(timer.current!); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs.length]);

  useEffect(() => {
    if (allSettled && timer.current) clearInterval(timer.current);
  }, [allSettled]);

  const handleKey = useCallback((e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    const node = dialogRef.current;
    const first = node?.querySelector(FOCUSABLE);
    (first || node)?.focus();
    return () => { previouslyFocused.current?.focus?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const els = Array.from(node.querySelectorAll(FOCUSABLE));
      if (!els.length) return;
      const firstEl = els[0];
      const lastEl = els[els.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    window.addEventListener('keydown', onTab);
    return () => window.removeEventListener('keydown', onTab);
  }, []);

  const stages = stagesFor(job, { warningCount: job?.warningCount });
  const scanning = !settled && job?.status !== 'queued';
  const queuedOnly = job?.status === 'queued';
  const stalled = queuedOnly && waited >= 30000;
  const risk = job?.risk_class;

  return createPortal(
    <div className="ingest-modal-overlay bi-console" onClick={onClose} role="presentation">
      <div
        className="ingest-proc"
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Processing uploaded document"
      >
        <div className="ing-scan-head">
          <span className="shield" aria-hidden="true">⛨</span>
          Scanning for hacks
        </div>

        <div className="ingest-proc-top">
          <div className={`ingest-scanner${scanning ? ' scanning' : ''}`} aria-hidden="true">
            <div className="ingest-scanner-beam" />
            {[12, 24, 36, 48, 60, 72, 84, 96, 108, 120].map((y) => (
              <div key={y} className="ingest-scanner-ln" style={{ top: y, width: y % 3 === 0 ? 48 : 72 }} />
            ))}
            <div className={`ingest-scanner-hl${job?.doc_type ? ' on' : ''}`} style={{ top: 24, width: 62 }} />
            <div className={`ingest-scanner-hl${stages[2]?.state === 'done' || stages[2]?.state === 'run' ? ' on' : ''}`} style={{ top: 60, width: 44 }} />
            <div className={`ingest-scanner-hl${stages[2]?.state === 'done' ? ' on' : ''}`} style={{ top: 96, width: 70 }} />
          </div>

          <div className="ingest-proc-info">
            <div className="ingest-proc-fn" title={current?.filename}>{current?.filename}</div>
            <div className="ingest-proc-sub">
              {[kb(job?.byte_size), extOf(current?.filename), 'uploaded just now'].filter(Boolean).join(' · ')}
            </div>

            {job?.doc_type ? (
              <div className="ingest-verdict">
                <div className="ingest-verdict-t">
                  <span className="ingest-verdict-name">{humanize(job.doc_type)}</span>
                  {risk ? <span className={`ingest-chip ingest-chip-${risk}`}>{risk}</span> : null}
                </div>
                {pct(job.doc_type_confidence) ? (
                  <div className="ingest-verdict-row">
                    <span>type confidence</span><span>{pct(job.doc_type_confidence)}</span>
                  </div>
                ) : null}
                {job.urgency_class ? (
                  <div className="ingest-verdict-row">
                    <span>urgency</span><span>{humanize(job.urgency_class)}</span>
                  </div>
                ) : null}
                {job.target_entity_type ? (
                  <div className="ingest-verdict-row">
                    <span>target entity</span>
                    <span>{[humanize(job.target_entity_type), pct(job.entity_confidence)].filter(Boolean).join(' · ')}</span>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="ingest-stages">
          {stages.map((s) => (
            <div key={s.name} className={`ingest-stage ${s.state}`}>
              <span className="ingest-stage-dot" aria-hidden="true">
                {s.state === 'done' ? '✓' : s.state === 'fail' ? '!' : ''}
              </span>
              <span className="ingest-stage-name">{s.name}</span>
              <span className="ingest-stage-out">{s.out}</span>
            </div>
          ))}
        </div>

        <div className="ingest-proc-foot">
          <span className="ingest-proc-note" data-testid="ingest-proc-note" aria-live="polite">
            {jobs.length > 1 ? `${index + 1} of ${jobs.length} · ` : ''}
            {settled
              ? (OUTCOME[job.status] || humanize(job.status))
              : stalled
                ? 'Still queued — no worker has picked this up. It stays in the queue either way.'
                : queuedOnly
                  ? 'Queued — waiting for a worker.'
                  : 'Nothing is written until you approve it.'}
          </span>
          {jobs.length > 1 && index < jobs.length - 1 ? (
            <button className="btn" onClick={() => setIndex((i) => i + 1)}>
              Next file
            </button>
          ) : null}
          {jobs.some((j) => j.job?.status === 'awaiting_review') ? (
            <button
              className="btn"
              onClick={() => { onClose(); try { (window as any).showPage?.('ingest'); } catch { /* not mounted */ } }}
            >
              View queue
            </button>
          ) : null}
          <button className="btn btn--primary" onClick={onClose}>
            {allSettled ? 'Done' : 'Close'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export { stagesFor, isSettledFn as isSettled };
